import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {api} from '../services/api';
import {HiEye, HiLogout, HiTrash, HiX, HiLockClosed} from 'react-icons/hi';

const ContactListPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const navigate = useNavigate();

  const loadContacts = async (page = 1) => {
    setLoading(true);
    try {
      const result = await api.getContacts(page, 10);
      if (result.success) {
        setContacts(result.data);
        setPagination(result.pagination);
        setCurrentPage(page);
      } else {
        if (result.message.includes('Unauthorized')) {
          navigate('/admin/login');
        }
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!api.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    loadContacts();
  }, [navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    setDeleteLoading(id);
    try {
      const result = await api.deleteContact(id);
      if (result.success) {
        loadContacts(currentPage);
      } else {
        alert(result.message || 'Failed to delete contact');
      }
    } catch (error) {
      alert('Failed to delete contact');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleLogout = () => {
    api.logout();
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-text-dark mb-2">
              Contact Management
            </h1>
            <p className="text-text-light">
              Total Contacts: {pagination.total_records || 0}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 rounded-xl bg-white text-text-dark hover:bg-gray-50 transition-all duration-300 font-medium"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/admin/change-password')}
              className="px-4 py-2 rounded-xl bg-white text-text-dark hover:bg-gray-50 transition-all duration-300 font-medium flex items-center gap-2"
            >
              <HiLockClosed size={18} />
              Change Password
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-xl bg-primary text-white hover:bg-primary-dark transition-all duration-300 font-medium flex items-center gap-2"
            >
              <HiLogout size={18} />
              Logout
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-text-light">Loading contacts...</p>
          </div>
        ) : contacts.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center">
            <p className="text-text-light text-lg">No contacts found</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-soft-pink border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-dark uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-dark uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-dark uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-dark uppercase tracking-wider">
                        Message
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-dark uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-text-dark uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contacts.map((contact) => (
                      <tr
                        key={contact.id}
                        className="hover:bg-cream transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-text-dark">
                            {contact.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            {contact.email}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-text-dark max-w-xs truncate">
                            {contact.subject || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-text-dark max-w-md">
                            <div className="line-clamp-2">
                              {contact.message || '-'}
                            </div>
                            {contact.message && contact.message.length > 100 && (
                              <button
                                onClick={() => setSelectedContact(contact)}
                                className="mt-1 text-xs text-primary hover:text-primary-dark flex items-center gap-1"
                              >
                                <HiEye size={14} />
                                View Full
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-text-light">
                            {formatDate(contact.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleDelete(contact.id)}
                            disabled={deleteLoading === contact.id}
                            className="inline-flex items-center justify-center p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete contact"
                          >
                            {deleteLoading === contact.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <HiTrash size={18} />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.total_pages > 1 && (
              <div className="mt-8 flex justify-center items-center gap-2">
                <button
                  onClick={() => loadContacts(currentPage - 1)}
                  disabled={!pagination.has_prev}
                  className="px-4 py-2 rounded-xl bg-white text-text-dark hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <div className="flex gap-2">
                  {[...Array(pagination.total_pages)].map((_, index) => {
                    const pageNum = index + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.total_pages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => loadContacts(pageNum)}
                          className={`w-10 h-10 rounded-xl transition-all duration-300 ${
                            pageNum === currentPage
                              ? 'bg-primary text-white'
                              : 'bg-white text-text-dark hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === currentPage - 2 ||
                      pageNum === currentPage + 2
                    ) {
                      return (
                        <span key={pageNum} className="w-10 h-10 flex items-center justify-center">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => loadContacts(currentPage + 1)}
                  disabled={!pagination.has_next}
                  className="px-4 py-2 rounded-xl bg-white text-text-dark hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Message Modal */}
      {selectedContact && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedContact(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-soft-pink px-6 py-4 flex justify-between items-start border-b border-gray-200">
              <div className="flex-1">
                <h3 className="text-xl font-display font-bold text-text-dark">
                  {selectedContact.name}
                </h3>
                <p className="text-sm text-text-light mt-1">
                  {formatDate(selectedContact.created_at)}
                </p>
              </div>
              <button
                onClick={() => setSelectedContact(null)}
                className="p-2 hover:bg-white rounded-full transition-colors"
              >
                <HiX size={24} className="text-text-dark" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <div className="space-y-4">
                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-text-light uppercase tracking-wider block mb-1">
                      Email
                    </label>

                      {selectedContact.email}
                  </div>

                </div>

                {/* Subject */}
                {selectedContact.subject && (
                  <div>
                    <label className="text-xs font-semibold text-text-light uppercase tracking-wider block mb-1">
                      Subject
                    </label>
                    <p className="text-sm text-text-dark font-medium">
                      {selectedContact.subject}
                    </p>
                  </div>
                )}

                {/* Message */}
                {selectedContact.message && (
                  <div>
                    <label className="text-xs font-semibold text-text-light uppercase tracking-wider block mb-2">
                      Message
                    </label>
                    <div className="bg-cream rounded-xl p-4">
                      <p className="text-sm text-text-dark whitespace-pre-wrap leading-relaxed">
                        {selectedContact.message}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => setSelectedContact(null)}
                className="px-4 py-2 rounded-xl bg-white text-text-dark hover:bg-gray-100 transition-all duration-300 font-medium"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDelete(selectedContact.id);
                  setSelectedContact(null);
                }}
                disabled={deleteLoading === selectedContact.id}
                className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-all duration-300 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <HiTrash size={18} />
                Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactListPage;
